// Customer.io — somente para o cliente Electrolux Instala. O template desse
// cliente carrega o snippet global window.analytics.
const CUSTOMER_IO_ENABLED = process.env.PROJECT_FOLDER === 'electroluxinstala'

// Controles mantidos somente durante a sessão atual do navegador:
// - lastUserSignature evita enviar duas vezes exatamente o mesmo perfil;
// - createdAtSentForUserId evita reenviar created_at para o mesmo usuário.
let lastUserSignature = null
let createdAtSentForUserId = null

// Ponto de entrada da integração. Confirma o cliente, lê o usuário que está
// atualmente no Vuex e inicia sua identificação no Customer.io.
export function bootstrapCustomerIo(store) {
	if (!CUSTOMER_IO_ENABLED) return

	try {
		const user = getUserFromStore(store)
		identifyCustomerIo(user)
	} catch {
		// A integração é opcional e não deve interromper o carregamento da aplicação.
	}
}

// Monta apenas os dados necessários ao Customer.io usando os getters do Vuex.
// O date_joined chega pela resposta de /accounts/read_user_data/ e é salvo
// no Vuex como created_at, nome usado pelo payload do Customer.io.
export function getUserFromStore(store) {
	return {
		id: store.getters['common/id'],
		email: store.getters['common/email'],
		full_name: store.getters['common/full_name'],
		created_at: store.getters['common/created_at'],
	}
}

// Compatibilidade entre o exemplo legado solicitado (_cio.identify({ id, ... }))
// e o snippet atual, cuja API recebe analytics.identify(userId, traits).
function getCustomerIoAdapter() {
	// O SDK só existe no navegador; durante build ou renderização sem window,
	// a integração é interrompida com segurança.
	if (typeof window === 'undefined') return null

	const analytics = window.analytics
	if (!analytics || typeof analytics.identify !== 'function') return null

	return {
		// Mantém internamente o formato legado _cio.identify({ id, ... }), mas
		// transforma a chamada para a assinatura do SDK atual:
		// analytics.identify(userId, traits).
		identify(profile) {
			const traits = {}

			// O id é o primeiro argumento da API atual. Os demais campos válidos
			// são enviados dentro do objeto traits.
			Object.keys(profile).forEach((key) => {
				if (key !== 'id' && profile[key] !== undefined && profile[key] !== null && profile[key] !== '') {
					traits[key] = profile[key]
				}
			})

			analytics.identify(profile.id, traits)
		},
	}
}

// Converte created_at para Unix timestamp em segundos, formato esperado pelo
// Customer.io. Aceita timestamp em segundos, milissegundos ou texto de data.
function getCreatedAt(value) {
	if (value === undefined || value === null || value === '') return

	const numericValue = Number(value)
	if (Number.isFinite(numericValue)) {
		// Aceita Unix timestamp em segundos ou milissegundos.
		return Math.floor(numericValue > 9999999999 ? numericValue / 1000 : numericValue)
	}

	const dateValue = new Date(value).getTime()
	if (Number.isFinite(dateValue)) return Math.floor(dateValue / 1000)
}

// Valida, formata e envia um usuário ao Customer.io.
export function identifyCustomerIo(user) {
	if (!CUSTOMER_IO_ENABLED || !user || user.id === undefined || user.id === null || user.id === '') return

	try {
		// Só continua quando o snippet do index.template.html criou
		// window.analytics e disponibilizou o método identify.
		const _cio = getCustomerIoAdapter()
		if (!_cio) return

		const profile = {
			id: String(user.id),
			created_at: getCreatedAt(user.created_at),
			email: user.email ? String(user.email) : undefined,
			first_name: undefined,
			last_name: undefined,
		}

		// O primeiro termo do nome completo vira first_name; todo o restante
		// permanece junto em last_name.
		if (user.full_name) {
			const fullName = String(user.full_name).trim()
			const nameParts = fullName.split(/\s+/)

			if (fullName) {
				profile.first_name = nameParts[0]
				if (nameParts.length > 1) profile.last_name = nameParts.slice(1).join(' ')
			}
		}

		// Impede chamadas repetidas causadas pelo boot inicial e pela mutation
		// do Vuex quando nenhuma informação do perfil mudou.
		const signature = JSON.stringify(profile)
		if (signature === lastUserSignature) return
		lastUserSignature = signature

		// O created_at deve ser enviado somente na primeira identificação do
		// usuário durante a sessão, evitando sobrescrever o atributo depois.
		const createdAt = createdAtSentForUserId === profile.id ? undefined : profile.created_at

		const identifyPayload = {
			id: profile.id,
			created_at: createdAt,
			email: profile.email,
			first_name: profile.first_name,
			last_name: profile.last_name,
		}

		// Esta é a chamada no formato legado. O adapter acima cuida
		// da conversão para analytics.identify(userId, traits).
		_cio.identify(identifyPayload)

		if (createdAt !== undefined) createdAtSentForUserId = profile.id
	} catch {
		// Uma falha no SDK não deve interromper o fluxo do usuário.
	}
}
